// ── Menu Background ── Cinematic camera orbit + atmosphere boost for main menu
// Controls the camera path while the menu is visible. Uses a CatmullRomCurve3
// spline through scenic map waypoints with smooth lookAt interpolation.

import * as THREE from 'three';

// ── Per-map camera orbit waypoints ──
// Each map gets a set of cinematic camera positions and a focus target.

interface MapCameraPath {
  waypoints: THREE.Vector3[];
  target: THREE.Vector3;
  /** How high the camera sits above the path Y */
  heightOffset: number;
}

const MAP_CAMERA_PATHS: Record<string, MapCameraPath> = {
  de_outpost_v6: {
    waypoints: [
      new THREE.Vector3(-30, 12, -40),
      new THREE.Vector3(-15, 10, -20),
      new THREE.Vector3(10, 14, -10),
      new THREE.Vector3(25, 11, 15),
      new THREE.Vector3(10, 13, 35),
      new THREE.Vector3(-20, 12, 25),
    ],
    target: new THREE.Vector3(0, 4, 0),
    heightOffset: 0,
  },
};

// Fallback path if map not found
const DEFAULT_PATH: MapCameraPath = MAP_CAMERA_PATHS.de_outpost_v6;

export class MenuBackground {
  private curve: THREE.CatmullRomCurve3;
  private target: THREE.Vector3;
  private t = 0;
  private speed = 0.008; // Full orbit ~125s
  private active = false;
  private camera: THREE.PerspectiveCamera | null = null;
  private savedCameraPos = new THREE.Vector3();
  private savedCameraQuat = new THREE.Quaternion();
  private targetQuat = new THREE.Quaternion();
  private lookAtMatrix = new THREE.Matrix4();
  private currentPos = new THREE.Vector3();

  // Subtle camera sway for organic feel
  private swayTime = 0;
  private swayOffset = new THREE.Vector3();

  constructor() {
    const path = DEFAULT_PATH;
    this.curve = new THREE.CatmullRomCurve3(path.waypoints, true, 'catmullrom', 0.5);
    this.target = path.target.clone();
  }

  /** Switch camera path for a different map. */
  setMap(mapId: string): void {
    const path = MAP_CAMERA_PATHS[mapId] ?? DEFAULT_PATH;
    this.curve = new THREE.CatmullRomCurve3(path.waypoints, true, 'catmullrom', 0.5);
    this.target.copy(path.target);
    this.t = 0;
  }

  /** Start the cinematic orbit. Saves camera state for restoration. */
  start(camera: THREE.PerspectiveCamera): void {
    this.camera = camera;
    this.savedCameraPos.copy(camera.position);
    this.savedCameraQuat.copy(camera.quaternion);
    this.active = true;
    this.t = Math.random(); // Start at random point on orbit
    this.swayTime = 0;

    // Jump camera to initial orbit position
    this.curve.getPointAt(this.t, this.currentPos);
    camera.position.copy(this.currentPos);
    camera.lookAt(this.target);
  }

  /** Stop orbit and restore camera to saved position. */
  stop(): void {
    if (this.camera) {
      this.camera.position.copy(this.savedCameraPos);
      this.camera.quaternion.copy(this.savedCameraQuat);
    }
    this.active = false;
    this.camera = null;
  }

  /** Update camera position along orbit path. Call in render loop. */
  update(dt: number): void {
    if (!this.active || !this.camera) return;

    // Advance along curve
    this.t = (this.t + this.speed * dt) % 1;
    this.curve.getPointAt(this.t, this.currentPos);

    // Add organic sway
    this.swayTime += dt * 0.3;
    this.swayOffset.set(
      Math.sin(this.swayTime * 0.7) * 0.3,
      Math.sin(this.swayTime * 0.5) * 0.15,
      Math.cos(this.swayTime * 0.6) * 0.25,
    );
    this.currentPos.add(this.swayOffset);

    // Smooth camera position (lerp for silky movement)
    this.camera.position.lerp(this.currentPos, 0.02);

    // Smooth lookAt using quaternion slerp
    this.lookAtMatrix.lookAt(this.camera.position, this.target, THREE.Object3D.DEFAULT_UP);
    this.targetQuat.setFromRotationMatrix(this.lookAtMatrix);
    this.camera.quaternion.slerp(this.targetQuat, 0.015);
  }

  isActive(): boolean {
    return this.active;
  }

  dispose(): void {
    this.stop();
  }
}
